import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { filterNil } from '@ngneat/elf';
import { partition } from 'lodash';
import { map } from 'rxjs';
import { AlertEntity, SpaceAlertEntity } from 'src/app/models/alert';
import { AlertService } from 'src/app/services/alert.service';
import {
  IconConfig,
  IconTypes,
} from 'src/app/standalones/components/pencil-icon/pencil-icon.component';
import { AlertViewModel } from '../../view-models/alert.view-model';
// Type-only: the space view models import this component, so a value import would cycle.
import type { SpaceAlertViewModel } from '../../view-models/space-alert.view-model';
import { RelatedLinkRowComponent } from '../related-link-row/related-link-row.component';
import { RelatedUserRowComponent } from '../related-user-row/related-user-row.component';

@Component({
  selector: 'app-related-space',
  templateUrl: './related-space.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, TranslateModule, RelatedUserRowComponent, RelatedLinkRowComponent],
})
export class RelatedSpaceComponent {
  readonly spaceIcon: IconConfig = { name: 'hash', type: IconTypes.lucideIcon };

  constructor(private readonly alertService: AlertService) {}

  /**
   * Read straight off the selected alert: GET /alerts nested-populates the space
   * and its users through populateAlertDetails, so the rows render without a
   * request of their own.
   */
  readonly relatedSpaceData$ = this.alertService.selectedAlert$.pipe(
    filterNil(),
    map((alert) => this.toRelatedSpaceData(alert)),
  );

  private toRelatedSpaceData(alert: AlertViewModel<AlertEntity>) {
    // Cast on the same grounds as AlertViewModelFactory.get: this component renders
    // only for the space-scoped alert types, which extend SpaceAlertViewModel.
    const spaceAlert = alert as SpaceAlertViewModel<SpaceAlertEntity>;
    const space = spaceAlert.entity?.spaceId;
    const [hosts, participants] = partition(space?.users ?? [], (spaceUser) => spaceUser.isOwner);

    // `space.users` is an array of `{ _id, isOwner }`, and `_id` is not an id:
    // ALERT_SPACE_POPULATE populates `users._id`, so it holds the user document
    // itself — or null once that user no longer exists.
    return {
      link: { text: spaceAlert.spaceName, url: spaceAlert.spaceUrl },
      hosts: hosts.map((spaceUser) => spaceUser._id),
      participants: participants.map((spaceUser) => spaceUser._id),
    };
  }
}
